import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { createOrgInvite, fetchOrgInvites, cancelOrgInvite } from "../api";
import Cookies from "js-cookie";
import { CreateInviteModal, CopyToast } from "../Modals/OrgInvitesPopup";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal, ChevronDown } from "lucide-react";
import { usePrivilege } from "../../../../guard/usePrivilege";
import { useTableSavedViews, TableSavedViewsControl } from "../../../TableSavedViews";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

// ✅ CONSTANTS AT TOP
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const SORTABLE_COLUMNS = {
  "Recipient Email": "recipientEmail",
  "Office Code": "newOfficeCode",
  "Created At": "createdAt",
  "Valid Till": "validUntil",
  "Status": "status",
  "Type": "inviteType",
};

// ✅ MAIN COMPONENT
const OrgInvitesTab = () => {
  // ✅ TABLE HEADERS (must match TABLE_HEADERS used in useTableSavedViews)
  const TABLE_HEADERS = [
    "Selection",
    "Action",
    "Recipient Email",
    "Office Code",
    "Created At",
    "Valid Till",
    "Status",
    "Type",
    "Link Expiry",
    "Copy Link",
  ];

  // ============================================
  // ✅ HELPER COMPONENTS (inline)
  // ============================================

  const DatePickerCalendar = ({ onSelectDate }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());

    const handleDayClick = (date) => {
      setSelectedDate(date);
      onSelectDate(date);
    };

    const handleGoToToday = () => {
      const today = new Date();
      setSelectedDate(today);
    };

    return (
      <div className="flex flex-col items-center bg-white dark:bg-acs-dark-surface rounded-lg p-4">
        <div className="mb-4">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDayClick}
            disabled={(date) => date < new Date()}
            classNames={{
              caption: "text-sm font-semibold text-gray-800 dark:text-acs-dark-text",
              head_cell: "text-xs font-medium text-gray-600 dark:text-gray-400 w-10 h-10",
              cell: "text-center text-sm w-10 h-10",
              day: "rounded hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer",
              day_selected: "bg-[#1e2a74] text-white font-bold",
              day_today: "border-2 border-blue-500",
              day_disabled: "text-gray-300 dark:text-gray-600 cursor-not-allowed",
            }}
          />
        </div>

        <button
          onClick={handleGoToToday}
          className="w-full py-2 px-4 bg-gray-100 dark:bg-acs-dark-border text-gray-700 dark:text-acs-dark-text rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-acs-dark-border/80 transition"
        >
          Go to today
        </button>

        {selectedDate && (
          <p className="mt-3 text-sm text-green-600 dark:text-green-400 font-medium">
            Selected: {selectedDate.toLocaleDateString()}
          </p>
        )}
      </div>
    );
  };

  const ExpiryDropdown = ({ invite, daysLeft, onRefresh }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          if (buttonRef.current && !buttonRef.current.contains(e.target)) {
            setIsOpen(false);
          }
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Save expiry date to backend
    const saveExpiryDate = async (newExpiryDate) => {
      setIsSaving(true);
      try {
        const token = Cookies.get("authToken");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/invites/extend-expiry?inviteUuid=${invite.uuid}&newExpiryDate=${newExpiryDate}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          onRefresh();
          setIsOpen(false);
          setShowCalendar(false);
        } else {
          alert("Failed to update expiry date");
        }
      } catch (error) {
        console.error("Error saving expiry:", error);
        alert("Error updating expiry date");
      } finally {
        setIsSaving(false);
      }
    };

    // Handle quick select (7, 30, 60 days)
    const handleQuickExtend = (days) => {
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + days);
      const dateString = newDate.toISOString().split("T")[0];
      saveExpiryDate(dateString);
    };

    // Handle "Remove expiration"
    const handleRemoveExpiration = () => {
      const farFutureDate = new Date("2099-12-31");
      const dateString = farFutureDate.toISOString().split("T")[0];
      saveExpiryDate(dateString);
    };

    // Calculate dropdown position
    const handleButtonClick = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setIsOpen(!isOpen);
    };

    return (
      <>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className="text-blue-600 hover:underline text-xs sm:text-sm flex items-center gap-1"
          disabled={isSaving}
        >
          {daysLeft === null
            ? "--"
            : daysLeft <= 0
            ? "Expired"
            : `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
          <ChevronDown size={14} />
        </button>

        {isOpen &&
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: dropdownPos.top,
                left: dropdownPos.left,
                zIndex: 9999,
              }}
              className="bg-white dark:bg-acs-dark-surface border border-gray-300 dark:border-acs-dark-border rounded-lg shadow-lg w-[310px]"
            >
              {showCalendar ? (
                <div className="p-3">
                  <button
                    onClick={() => setShowCalendar(false)}
                    className="text-sm text-gray-600 dark:text-gray-400 mb-2 hover:text-gray-800"
                  >
                    ← Back
                  </button>
                  <DatePickerCalendar
                    onSelectDate={(selectedDate) => {
                      const dateString = selectedDate.toISOString().split("T")[0];
                      saveExpiryDate(dateString);
                    }}
                  />
                </div>
              ) : (
                <div className="py-2">
                  <button
                    onClick={() => handleQuickExtend(7)}
                    disabled={isSaving}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-acs-dark-border text-sm text-gray-700 dark:text-acs-dark-text disabled:opacity-50"
                  >
                    Extend by 7 days
                  </button>

                  <button
                    onClick={() => handleQuickExtend(30)}
                    disabled={isSaving}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-acs-dark-border text-sm text-gray-700 dark:text-acs-dark-text disabled:opacity-50"
                  >
                    Extend by 30 days
                  </button>

                  <button
                    onClick={() => handleQuickExtend(60)}
                    disabled={isSaving}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-acs-dark-border text-sm text-gray-700 dark:text-acs-dark-text disabled:opacity-50"
                  >
                    Extend by 60 days
                  </button>

                  <hr className="my-2 border-gray-200 dark:border-acs-dark-border" />

                  <button
                    onClick={() => setShowCalendar(true)}
                    disabled={isSaving}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-acs-dark-border text-sm text-gray-700 dark:text-acs-dark-text disabled:opacity-50"
                  >
                    Select a date
                  </button>

                  <hr className="my-2 border-gray-200 dark:border-acs-dark-border" />

                  <button
                    onClick={handleRemoveExpiration}
                    disabled={isSaving}
                    className="w-full text-left px-4 py-2 hover:bg-red-100 dark:hover:bg-red-900/20 text-sm text-red-600 dark:text-red-400 disabled:opacity-50"
                  >
                    Remove expiration
                  </button>
                </div>
              )}
            </div>,
            document.body
          )}
      </>
    );
  };

  // ✅ STATE DECLARATIONS
  const [invites, setInvites] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [ascending, setAscending] = useState(true);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ days: 7, email: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [copyMsg, setCopyMsg] = useState("");
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // ✅ useTableSavedViews Hook (for column customization)
  const tableViews = useTableSavedViews({
    pageKey: "org-invites-list",
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2, // Selection, Action are always visible
    defaultStickyColumnKeys: ["Selection", "Action", "Recipient Email"],
    getExtraConfig: () => ({
      currentPage: page,
      pageSize: pageSize,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setPageSize(config.pageSize);
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // ✅ REFS
  const dropdownRef = useRef(null);
  const checkboxHeaderRef = useRef(null);

  // ✅ HOOKS AND HELPERS
  const { hasPrivilege } = usePrivilege();
  const canCreateOrgInvite = hasPrivilege("orgInvites:CREATE");
  const officeCode = Cookies.get("officeCode");

  // ✅ FETCH INVITES ON MOUNT
  useEffect(() => {
    setFormLoading(true);
    fetchOrgInvites(officeCode)
      .then((res) => setInvites(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setInvites([]))
      .finally(() => setFormLoading(false));
  }, [officeCode]);

  // ✅ CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // ✅ SORTING LOGIC
  const handleSort = (columnHeader) => {
    if (sortBy === columnHeader) {
      setAscending(!ascending);
    } else {
      setSortBy(columnHeader);
      setAscending(true);
    }
    setPage(1);
    markDirty();
  };

  // ✅ ACTION MENU CLICK
  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === id ? null : id));
  };

  // ✅ SORTING: Apply sort to invites
  const safeInvites = Array.isArray(invites) ? invites : [];
  let effectiveInvites = safeInvites;

  if (sortBy && SORTABLE_COLUMNS[sortBy]) {
    const sortKey = SORTABLE_COLUMNS[sortBy];
    effectiveInvites = [...safeInvites].sort((a, b) => {
      let aVal = a?.[sortKey];
      let bVal = b?.[sortKey];

      if (!aVal || !bVal) return 0;

      // Handle date fields
      if (sortKey === "createdAt" || sortKey === "validUntil") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle status ordering
      if (sortKey === "status") {
        const statusOrder = { "Pending": 1, "Onboarding Complete": 2, "Expired": 3, "Cancelled": 4 };
        aVal = statusOrder[aVal] || 999;
        bVal = statusOrder[bVal] || 999;
      }

      if (aVal < bVal) return ascending ? -1 : 1;
      if (aVal > bVal) return ascending ? 1 : -1;
      return 0;
    });
  }

  const sorted = effectiveInvites;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  // ✅ EVENT HANDLERS

  const handleSelectAll = (checked) =>
    setSelectedInvites(checked ? safeInvites.map((i) => i.uuid) : []);

  const handleSingleCheckbox = (uuid) =>
    setSelectedInvites((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );

  const handleCopy = (invite) => {
    const validUntil = invite?.validUntil || invite?.validTill;
    if (validUntil && new Date(validUntil) < new Date()) {
      setCopyMsg("Invite expired");
      setTimeout(() => setCopyMsg(""), 2000);
      return;
    } else if (invite.used) {
      setCopyMsg("Invite is already used");
      setTimeout(() => setCopyMsg(""), 2000);
      return;
    } else if (invite.cancelled) {
      setCopyMsg("Invite is cancelled");
      setTimeout(() => setCopyMsg(""), 2000);
      return;
    }
    const text =
      invite?.link ||
      invite?.inviteLink ||
      (invite?.uuid
        ? `${process.env.REACT_APP_API_BASE_URL}/api/invites/validate-code?inviteCode=${invite.uuid}&type=${invite.inviteType || "orgInvite"}`
        : "");
    if (text) {
      navigator.clipboard?.writeText(text).catch(() => {});
      setCopyMsg("Invite link copied!");
    } else {
      setCopyMsg("No invite link available");
    }
    setTimeout(() => setCopyMsg(""), 2000);
  };

  const handleFormChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (form.days < 1 || form.days > 365) return alert("Days must be 1-365");
    setFormLoading(true);
    try {
      await createOrgInvite(officeCode, form.inviteType, form.days, form.email);
      setShowForm(false);
      setForm({ days: 7, email: "" });
      fetchOrgInvites(officeCode).then((res) =>
        setInvites(Array.isArray(res?.data) ? res.data : [])
      );
    } finally {
      setFormLoading(false);
    }
  };

  // ✅ RETURN JSX
  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {/* Column Views Control */}
      <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
        <TableSavedViewsControl
          controlLabel={controlLabel}
          views={views}
          activeView={activeView}
          loading={viewsLoading}
          error={null}
          isDirty={isDirty}
          allDataHeaders={TABLE_HEADERS.slice(2)}
          dataHeaders={dataHeaders}
          onSelectView={selectView}
          onSaveNew={saveNewView}
          onUpdateActive={updateActiveView}
          onRename={renameView}
          onDelete={deleteView}
          onSetDefault={setDefaultView}
          onUpdateColumnVisibility={updateColumnVisibility}
          onManageColumns={onManageColumns}
        />
      </div>

      {/* Table Container */}
      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
        <Table
          {...columnManagerProps}
          headers={TABLE_HEADERS}
          data={paged}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => {}}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selectedInvites}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          nonHideableColumnIds={["Selection", "Action", "Recipient Email"]}
          sortBy={sortBy}
          ascending={ascending}
          onSort={handleSort}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
        >
          {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
            const id = row.uuid;
            const isOpen = openDropdownRow === id;
            const isExpired = new Date(row.validUntil) < new Date();
            const canCancel = !row.cancelled && !row.used && !isExpired;

            const actions = [
              {
                icon: "Copy",
                label: "Copy Link",
                color: "gray",
                action: () => handleCopy(row),
              },
              ...(canCancel
                ? [
                    {
                      icon: "X",
                      label: "Cancel Invite",
                      color: "red",
                      action: async () => {
                        try {
                          await cancelOrgInvite(row);
                          fetchOrgInvites(officeCode).then((res) =>
                            setInvites(Array.isArray(res?.data) ? res.data : [])
                          );
                        } catch {
                          alert("Failed to cancel invite");
                        }
                        setOpenDropdownRow(null);
                      },
                    },
                  ]
                : []),
            ];

            const statusText =
              isExpired && !row.used && !row.cancelled
                ? "Expired"
                : row.used
                ? "Onboarding Complete"
                : row.cancelled
                ? "Cancelled"
                : "Pending";

            return (
              <>
                {/* Column 0 — Checkbox */}
                <td
                  className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                  style={{
                    width: "80px",
                    minWidth: "80px",
                    maxWidth: "80px",
                    boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                  }}
                >
                  <input
                    type="checkbox"
                    className="cursor-pointer mx-auto block"
                    checked={selectedInvites.includes(row.uuid)}
                    onChange={() => handleSingleCheckbox(row.uuid)}
                  />
                </td>

                {/* Column 1 — Action */}
                <td
                  onClick={(e) => e.stopPropagation()}
                  className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                  style={{
                    width: "50px",
                    minWidth: "50px",
                    maxWidth: "50px",
                    boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                  }}
                >
                  <button
                    data-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleActionClick(id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {isOpen &&
                  ReactDOM.createPortal(
                    <div
                      ref={dropdownRef}
                      style={{
                        position: "absolute",
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        zIndex: 9999,
                      }}
                    >
                      <ActionMenu
                        rowId={id}
                        isOpen
                        onClose={() => setOpenDropdownRow(null)}
                        moduleName="OrgInvite"
                        actions={actions}
                      />
                    </div>,
                    document.body
                  )}

                {/* Data Columns */}
                {(() => {
                  // Use visibleColumns if available (respects pin/hide/reorder), otherwise use dataHeaders
                  const orderedDataHeaders = (visibleColumns ?? [])
                    .map((col) => col.id)
                    .filter((id) => id !== "Selection" && id !== "Action");
                  const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                  return dataHeadersToRender.map((header) => {
                    const { className: colClass, style: colStyle } = getStickyCellProps(header);

                    let content;
                    switch (header) {
                      case "Recipient Email":
                        content = row.recipientEmail || "--";
                        break;
                      case "Office Code":
                        content = row.newOfficeCode || row.officeCode || "--";
                        break;
                      case "Created At":
                        content = new Date(row.createdAt).toLocaleString();
                        break;
                      case "Valid Till":
                        content = new Date(row.validUntil).toLocaleString();
                        break;
                      case "Status":
                        content = statusText;
                        break;
                      case "Type":
                        content = row.inviteType || "--";
                        break;
                      case "Link Expiry": {
                        const validUntil = row.validUntil ? new Date(row.validUntil) : null;
                        const now = new Date();
                        const daysLeft = validUntil
                          ? Math.floor((validUntil - now) / (1000 * 60 * 60 * 24))
                          : null;
                        content = (
                          <ExpiryDropdown
                            invite={row}
                            daysLeft={daysLeft}
                            onRefresh={() => {
                              fetchOrgInvites(officeCode).then((res) =>
                                setInvites(Array.isArray(res?.data) ? res.data : [])
                              );
                            }}
                          />
                        );
                        break;
                      }
                      case "Copy Link":
                        content = (
                          <button
                            className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-none"
                            onClick={() => handleCopy(row)}
                          >
                            Copy Link
                          </button>
                        );
                        break;
                      default:
                        content = "--";
                    }

                    return (
                      <td
                        key={header}
                        className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                        style={{
                          ...colStyle,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {content}
                      </td>
                    );
                  });
                })()}
              </>
            );
          }}
        </Table>
      </div>

      {/* Pagination */}
      <GlobalPagination
        label="Invites per page"
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={pageSize}
        onItemsPerPageChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        options={PAGE_SIZE_OPTIONS}
      />

      {/* Create Button */}
      {canCreateOrgInvite && (
        <button
          className="fixed bottom-8 right-8 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition"
          onClick={() => setShowForm(true)}
          title="Create New Invitation"
        >
          <svg
            className="w-7 h-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Modals */}
      {showForm && (
        <CreateInviteModal
          form={form}
          formLoading={formLoading}
          onClose={() => setShowForm(false)}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
        />
      )}

      {copyMsg && <CopyToast message={copyMsg} />}
    </div>
  );
};

export default OrgInvitesTab;